import { Component, OnInit } from '@angular/core';
import { UserService } from '../../../layout/service/user.service';
import { ChartModule } from 'primeng/chart';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { CommonModule } from '@angular/common';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { ProgressBarModule } from 'primeng/progressbar';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { TDocumentDefinitions } from 'pdfmake/interfaces';

pdfMake.vfs = pdfFonts['vfs'] as unknown as { [file: string]: string };
@Component({
    selector: 'app-result',
    standalone: true,
    imports: [
        ChartModule,
        TableModule,
        ButtonModule,
        InputTextModule,
        CommonModule,
        ConfirmDialog,
        ToastModule,
        ProgressBarModule,
    ],
    providers: [ConfirmationService, MessageService],
    template: `
        <p-toast />
        <p-confirmdialog />
        <div class="card">
            <p-progressbar *ngIf="fetchingData" mode="indeterminate" [style]="{ height: '6px' }" />
            <h3 class="text-primary mb-4">OSCE Result Test</h3>
            <div class="grid grid-cols-12 gap-4 mb-3">
                <div class="col-span-6">
                    <p-chart type="bar" [data]="barChartData" [options]="barChartOptions"></p-chart>
                </div>
                <div class="col-span-6">
                    <p-chart type="bar" [data]="attemptsChartData" [options]="attemptsChartOptions"></p-chart>
                </div>
            </div>
            <p-table #dt [value]="results" stripedRows [tableStyle]="{ 'min-width': '50rem' }" [rowHover]="true" [rows]="10"
                [showCurrentPageReport]="true" [rowsPerPageOptions]="[10, 25, 50]" [loading]="loading"
                [paginator]="true" currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                [filterDelay]="0" [globalFilterFields]="['KATEGORI','JUDUL','UNIVERSITAS','CREATED_BY']"
                >

                <ng-template pTemplate="caption">
                    <div class="flex items-center">
                        <div>
                            <input pInputText type="text" (input)="onInput($event, dt)" placeholder="Search keyword" />
                        </div>
                    </div>
                </ng-template>

                <ng-template #header>
                    <tr>
                        <th class="w-20 text-center">No</th>
                        <th pSortableColumn="CREATED_AT">Date <p-sortIcon field="CREATED_AT"></p-sortIcon></th>
                        <th pSortableColumn="KATEGORI">Category <p-sortIcon field="KATEGORI"></p-sortIcon></th>
                        <th pSortableColumn="JUDUL">Title <p-sortIcon field="JUDUL"></p-sortIcon></th>
                        <th pSortableColumn="MAHASISWA">Name <p-sortIcon field="MAHASISWA"></p-sortIcon></th>
                        <th pSortableColumn="BOBOT" class="w-20">Bobot <p-sortIcon field="BOBOT"></p-sortIcon></th>
                        <th pSortableColumn="SKOR" class="w-20">Skor <p-sortIcon field="SKOR"></p-sortIcon></th>
                        <th pSortableColumn="UNIVERSITAS">Universitas <p-sortIcon field="UNIVERSITAS"></p-sortIcon></th>
                        <th pSortableColumn="CREATED_BY">Tutor <p-sortIcon field="CREATED_BY"></p-sortIcon></th>
                        <th class="w-20">Actions</th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-result let-i="rowIndex">
                    <tr>
                        <td>{{ i + 1 }}</td>
                        <td>{{ result.CREATED_AT | date: 'yyyy-MM-dd' }}</td>
                        <td>{{ result.KATEGORI }}</td>
                        <td>{{ result.JUDUL }}</td>
                        <td>{{ result.MAHASISWA }}</td>
                        <td>{{ result.BOBOT }}</td>
                        <td>{{ result.SKOR }}</td>
                        <td>{{ result.UNIVERSITAS }}</td>
                        <td>{{ result.CREATED_BY }}</td>
                        <td class="w-40">
                            <p-button icon="pi pi-file-pdf" variant="text" [raised]="true" severity="danger" class="mr-2" [disabled]="fetchingData" (click)="onPrint(result)"></p-button>
                            <p-button icon="pi pi-trash" severity="danger" class="mr-2" (click)="onDelete($event, result)" [disabled]="fetchingData"></p-button>
                        </td>
                    </tr>
                </ng-template>
            </p-table>
        </div>
    `
})
export class Result implements OnInit {
    results: any[] = [];
    loading: boolean = true;
    fetchingData: boolean = false; // New state to track data fetching
    categories: string[] = [];
    categoryCounts: { [key: string]: number } = {};

    barChartData: any = {
        labels: [],
        datasets: [
            {
                data: [],
                label: 'Average Score',
                backgroundColor: '#3b82f6',
                hoverBackgroundColor: '#2563eb'
            }
        ]
    };

    attemptsChartData: any = {
        labels: [],
        datasets: [
            {
                data: [],
                label: 'Attempts',
                backgroundColor: '#f59e0b',
                hoverBackgroundColor: '#d97706'
            }
        ]
    };

    barChartOptions: any = {
        responsive: true,
        plugins: {
            legend: { display: true },
            title: {
                display: true,
                text: 'Skor OSCE per Kategori'
            }
        },
        scales: {
            y: { beginAtZero: true }
        }
    };

    attemptsChartOptions: any = {
        responsive: true,
        plugins: {
            legend: { display: true },
            title: {
                display: true,
                text: 'Attempts per Category'
            }
        },
        scales: {
            y: { beginAtZero: true }
        }
    };

    constructor(
        private userService: UserService,
        public messageService: MessageService,
        private confirmationService: ConfirmationService
    ) { }

    ngOnInit(): void {
        this.fetchData();
    }

    fetchData(): void {
        this.fetchingData = true; // Show progress bar and disable buttons
        this.userService.getOsceResult().then((response) => {
            this.results = response.data;
            this.loading = false;

            // Group scores by category and calculate the average
            const categoryScores: { [key: string]: number[] } = {};
            this.results.forEach(item => {
                if (!categoryScores[item.KATEGORI]) {
                    categoryScores[item.KATEGORI] = [];
                }
                categoryScores[item.KATEGORI].push(Number(item.SKOR));
            });

            this.categories = Object.keys(categoryScores);
            const averages = this.categories.map(category => {
                const scores = categoryScores[category];
                return scores.reduce((sum, score) => sum + score, 0) / scores.length;
            });

            // Calculate the count of attempts per category
            this.categoryCounts = this.categories.reduce((counts, category) => {
                counts[category] = categoryScores[category].length;
                return counts;
            }, {} as { [key: string]: number });

            // Ensure barChartData is in the correct format
            this.barChartData = {
                labels: this.categories,
                datasets: [
                    {
                        data: averages,
                        label: 'Average Score',
                        backgroundColor: '#3b82f6',
                        hoverBackgroundColor: '#2563eb'
                    }
                ]
            };

            this.attemptsChartData = {
                labels: this.categories,
                datasets: [
                    {
                        data: this.categories.map(category => this.categoryCounts[category]),
                        label: 'Attempts',
                        backgroundColor: '#f59e0b',
                        hoverBackgroundColor: '#d97706'
                    }
                ]
            };
        }).finally(() => {
            this.fetchingData = false; // Hide progress bar and enable buttons
        });
    }

    onInput(event: Event, dt: any) {
        const inputElement = event.target as HTMLInputElement;
        dt.filterGlobal(inputElement.value, 'contains');
    }

    onDelete(event: Event, result: any) {
        this.confirmationService.confirm({
            target: event.target as EventTarget,
            message: 'Do you want to delete this record?',
            header: 'Danger Zone',
            icon: 'pi pi-info-circle',
            rejectLabel: 'Cancel',
            rejectButtonProps: {
                label: 'Cancel',
                severity: 'secondary',
                outlined: true,
            },
            acceptButtonProps: {
                label: 'Delete',
                severity: 'danger',
            },

            accept: () => {
                this.fetchingData = true; // Show progress bar and disable buttons
                this.userService.deleteOsceResult({ ID_REPORT: result.ID_REPORT }).then(() => {
                    this.fetchData(); // Refetch data after deletion
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Result deleted successfully' });
                }).catch(() => {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete result' });
                }).finally(() => {
                    this.fetchingData = false; // Hide progress bar and enable buttons
                });
            },
            reject: () => {
                this.messageService.add({ severity: 'error', summary: 'Rejected', detail: 'You have rejected' });
            },
        });
    }

    onPrint(result: any): void {
        this.fetchingData = true;
        this.userService.detailOsceResult({ ID_REPORT: result.ID_REPORT }).then((response) => {
            const detail = response.data;

            // Kelompokkan data
            const headersAndActivities: any[] = [];
            detail.forEach((item: any) => {
                if (item.IS_HEADER === '1') {
                    headersAndActivities.push({ type: 'header', html: item.ACTIVITY });
                } else {
                    headersAndActivities.push({
                        type: 'activity',
                        html: item.ACTIVITY,
                        skor: item.BOBOT,
                        komentar: item.KOMENTAR || ''
                    });
                }
            });

            // Helper: Convert HTML to pdfmake ops (basic tags: b, strong, ul, ol, li, p)
            function htmlToPdfMakeOps(html: string): any {
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                function parseNode(node: any): any {
                    if (!node) return '';
                    if (node.nodeType === 3) return node.textContent; // text node
                    if (node.nodeType !== 1) return '';
                    const tag = node.tagName.toLowerCase();
                    if (tag === 'b' || tag === 'strong') {
                        return { text: node.textContent, bold: true };
                    }
                    if (tag === 'ul' || tag === 'ol') {
                        const items = Array.from(node.children).filter((n: any) => n.tagName && n.tagName.toLowerCase() === 'li');
                        return {
                            [tag === 'ul' ? 'ul' : 'ol']: items.map((li: any) => parseNode(li))
                        };
                    }
                    if (tag === 'li') {
                        return Array.from(node.childNodes).map(parseNode);
                    }
                    if (tag === 'p') {
                        return { text: node.textContent, margin: [0, 2, 0, 2] };
                    }
                    // fallback: parse children
                    return Array.from(node.childNodes).map(parseNode);
                }
                const body = doc.body;
                const children = Array.from(body.childNodes).map(parseNode);
                // Flatten nested arrays
                function flatten(arr: any): any {
                    return Array.isArray(arr) ? arr.reduce((a, b) => a.concat(flatten(b)), []) : [arr];
                }
                return flatten(children).filter((x: any) => x && (typeof x === 'string' || typeof x === 'object'));
            }

            const content: any[] = [
                { text: 'Laporan Hasil OSCE', style: 'header' },
                { text: `ID Laporan: ${result.ID_REPORT}`, margin: [0, 10, 0, 0] },
                { text: `Kategori: ${result.KATEGORI}` },
                { text: `Judul: ${result.JUDUL}` },
                { text: `Nama: ${result.MAHASISWA}` },
                { text: `Universitas: ${result.UNIVERSITAS}` },
                { text: `Tutor: ${result.CREATED_BY}` },
                { text: `Tanggal: ${result.CREATED_AT}`, margin: [0, 0, 0, 10] },
            ];

            // Tambah tabel
            content.push({
                table: {
                    widths: ['*', 30, 30, 30, 30, '*'],
                    body: [
                        [
                            { text: 'KEGIATAN', bold: true },
                            { text: '0', alignment: 'center', bold: true },
                            { text: '1', alignment: 'center', bold: true },
                            { text: '2', alignment: 'center', bold: true },
                            { text: '3', alignment: 'center', bold: true },
                            { text: 'KOMENTAR', bold: true }
                        ],
                        ...headersAndActivities.map(item => {
                            if (item.type === 'header') {
                                return [
                                    { stack: htmlToPdfMakeOps(item.html), colSpan: 6, bold: true, fillColor: '#f0f0f0' }, '', '', '', '', ''
                                ];
                            } else {
                                const checkmark = '√';
                                return [
                                    { stack: htmlToPdfMakeOps(item.html) },
                                    { text: item.skor === '0' ? checkmark : '', alignment: 'center' },
                                    { text: item.skor === '1' ? checkmark : '', alignment: 'center' },
                                    { text: item.skor === '2' ? checkmark : '', alignment: 'center' },
                                    { text: item.skor === '3' ? checkmark : '', alignment: 'center' },
                                    item.komentar
                                ];
                            }
                        })
                    ]
                },
                layout: 'grid',
                margin: [0, 10, 0, 0]
            });

            const documentDefinition: TDocumentDefinitions = {
                pageOrientation: 'landscape',
                pageSize: 'A4',
                content,
                styles: {
                    header: {
                        fontSize: 18,
                        bold: true,
                        margin: [0, 0, 0, 10]
                    }
                },
                defaultStyle: {
                    fontSize: 9 // Perkecil font agar muat di kertas
                }
            };
            const filename = `${result.MAHASISWA}-${result.JUDUL}-${result.CREATED_AT}`.replace(/[\s:]/g, '_') + '.pdf';

            pdfMake.createPdf(documentDefinition).download(filename);
        }).catch(() => {
            this.messageService.add({ severity: 'error', summary: 'Gagal', detail: 'Gagal membuat PDF.' });
        }).finally(() => {
            this.fetchingData = false;
        });
    }

}
